import { ref, push, set, onValue, get, query, orderByChild, update, onDisconnect, serverTimestamp } from 'firebase/database';
import FirebaseAuthService, { firebaseDatabase } from './FirebaseAuthService';

// Firebase support user ID from users/support collection
const SUPPORT_USER_ID = 'support';

class LiveSupportService {
    constructor() {
        // Use the shared database from FirebaseAuthService so auth state is shared
        this.db = firebaseDatabase;
        this.activeConnections = new Map();
    }

    getDatabase() {
        // Always use the shared instance from FirebaseAuthService
        if (!this.db) {
            this.db = firebaseDatabase;
        }
        return this.db;
    }

    /**
     * Get or create a live support chat thread for a user
     * @param {string} userId - User ID to create/get support chat for
     * @param {Object} userInfo - User information (name, email, etc.)
     * @returns {Promise<Object>} Chat thread information
     */
    /**
     * Ensure support user exists in Firebase users collection
     * @returns {Promise<boolean>} True if support user exists or was created
     */
    async ensureSupportUserExists() {
        try {
            // Ensure Firebase authentication before accessing database
            const isAuthenticated = await FirebaseAuthService.ensureAuthentication();
            if (!isAuthenticated) {
                console.log('❌ [LIVE SUPPORT] Firebase authentication required');
                return false;
            }

            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            // Check if support user exists in users collection (as per latest.json)
            const supportUserRef = ref(this.getDatabase(), 'users/support');
            const supportUserSnapshot = await get(supportUserRef);

            if (!supportUserSnapshot.exists()) {
                // Create support user in users collection
                const supportUserData = {
                    id: 'support',
                    name: 'WearIt Support',
                    email: 'support@wearit.me',
                    role: 'support',
                    avatar: '',
                    description: 'Official WearIt Support Team',
                    isOnline: false,
                    lastSeen: Date.now(),
                    createdAt: Date.now()
                };

                await set(supportUserRef, supportUserData);
                console.log('✅ [DASHBOARD] Support user created in Firebase users collection');
            }

            return true;
        } catch (error) {
            console.log('Error ensuring support user exists:', error);
            return false;
        }
    }

    async getOrCreateSupportChat(userId, userInfo = {}) {
        try {
            // Ensure authentication
            const isAuthenticated = await FirebaseAuthService.ensureAuthentication();
            if (!isAuthenticated) {
                throw new Error('Authentication required');
            }

            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            // Ensure support user exists first
            await this.ensureSupportUserExists();

            // Generate support chat ID (support + user)
            const supportChatId = `support_${SUPPORT_USER_ID}_${userId}`;

            // Check if chat already exists
            const chatRef = ref(this.getDatabase(), `liveSupport/${supportChatId}`);
            const chatSnapshot = await get(chatRef);

            if (chatSnapshot.exists()) {
                // Chat exists, return existing data
                const chatData = chatSnapshot.val();
                return {
                    chatId: supportChatId,
                    exists: true,
                    ...chatData
                };
            } else {
                // Get support user data from users collection
                const supportUserRef = ref(this.getDatabase(), 'users/support');
                const supportUserSnapshot = await get(supportUserRef);
                const supportUserData = supportUserSnapshot.exists() ? supportUserSnapshot.val() : {
                    id: 'support',
                    name: 'WearIt Support',
                    role: 'support'
                };

                // Create new support chat
                const chatData = {
                    id: supportChatId,
                    type: 'live_support',
                    participants: [SUPPORT_USER_ID, userId],
                    participantsInfo: {
                        [SUPPORT_USER_ID]: {
                            id: SUPPORT_USER_ID,
                            name: supportUserData.name || 'WearIt Support',
                            role: 'support',
                            isOnline: false,
                            lastSeen: null,
                            unread: false,
                            avatar: supportUserData.avatar || '',
                            email: supportUserData.email || 'support@wearit.me'
                        },
                        [userId]: {
                            id: userId,
                            name: userInfo.name || userInfo.username || 'User',
                            email: userInfo.email || '',
                            role: 'user',
                            isOnline: false,
                            lastSeen: null,
                            unread: false,
                            avatar: userInfo.avatar || ''
                        }
                    },
                    status: 'active',
                    createdAt: serverTimestamp(),
                    lastUpdated: serverTimestamp(),
                    lastMessage: 'Support chat started',
                    lastMessageTimestamp: serverTimestamp(),
                    lastMessageSenderId: SUPPORT_USER_ID,
                    messages: {}
                };

                await set(chatRef, chatData);

                // Send initial welcome message
                await this.sendMessage(supportChatId, SUPPORT_USER_ID, 'Hello! How can I help you today?', 'text');

                return {
                    chatId: supportChatId,
                    exists: false,
                    ...chatData
                };
            }
        } catch (error) {
            console.log('Error getting/creating support chat:', error);
            throw error;
        }
    }

    /**
     * Send a message in live support chat
     * @param {string} chatId - Support chat ID
     * @param {string} senderId - Sender ID (admin or user)
     * @param {string} message - Message content
     * @param {string} type - Message type (text, image, etc.)
     * @returns {Promise<Object>} Message data
     */
    async sendMessage(chatId, senderId, message, type = 'text') {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            // Prevent duplicate messages by checking if we're already sending
            const sendingKey = `${chatId}_${senderId}_${message}_${Date.now()}`;
            if (this.sendingMessages && this.sendingMessages.has(sendingKey)) {
                console.warn('Message already being sent, skipping duplicate');
                return { success: false, error: 'Message already being sent' };
            }

            // Initialize sending messages tracker
            if (!this.sendingMessages) {
                this.sendingMessages = new Set();
            }
            this.sendingMessages.add(sendingKey);

            const timestamp = Date.now();
            const messageData = {
                senderId: senderId,
                message: message,
                type: type,
                timestamp: timestamp,
                read: false
            };

            // Add message to chat using atomic operation
            const messagesRef = ref(this.getDatabase(), `liveSupport/${chatId}/messages`);
            const newMessageRef = push(messagesRef);
            messageData.id = newMessageRef.key;

            // Use atomic write to prevent race conditions
            await set(newMessageRef, messageData);

            // Update chat metadata in a separate operation
            const updates = {};
            updates[`liveSupport/${chatId}/lastMessage`] = message;
            updates[`liveSupport/${chatId}/lastMessageTimestamp`] = timestamp;
            updates[`liveSupport/${chatId}/lastMessageSenderId`] = senderId;
            updates[`liveSupport/${chatId}/lastUpdated`] = timestamp;

            // Mark as unread for receiver
            const receiverId = senderId === SUPPORT_USER_ID ?
                chatId.split('_')[2] : SUPPORT_USER_ID; // Extract user ID from chat ID



            updates[`liveSupport/${chatId}/participantsInfo/${receiverId}/unread`] = true;
            updates[`liveSupport/${chatId}/participantsInfo/${senderId}/unread`] = false;

            // Apply all updates atomically
            await update(ref(this.getDatabase()), updates);

            // Remove from sending tracker
            this.sendingMessages.delete(sendingKey);

            return { success: true, data: messageData };
        } catch (error) {
            console.log('Error sending message:', error);
            // Remove from sending tracker on error
            if (this.sendingMessages) {
                const sendingKey = `${chatId}_${senderId}_${message}_${Date.now()}`;
                this.sendingMessages.delete(sendingKey);
            }
            throw error;
        }
    }

    /**
     * Listen to messages in real-time for live support chat
     * @param {string} chatId - Support chat ID
     * @param {Function} callback - Callback function to handle messages
     * @returns {Function} Unsubscribe function
     */
    listenToMessages(chatId, callback) {
        try {
            if (!this.getDatabase()) {
                return () => { };
            }

            // Clean up any existing connection for this chat
            if (this.activeConnections.has(chatId)) {
                const existingUnsubscribe = this.activeConnections.get(chatId);
                if (typeof existingUnsubscribe === 'function') {
                    existingUnsubscribe();
                }
                this.activeConnections.delete(chatId);
            }

            const messagesRef = ref(this.getDatabase(), `liveSupport/${chatId}/messages`);

            const unsubscribe = onValue(messagesRef, (snapshot) => {
                try {
                    const messages = [];
                    if (snapshot.exists()) {
                        snapshot.forEach((childSnapshot) => {
                            const messageData = childSnapshot.val();
                            if (messageData && childSnapshot.key) {
                                messages.push({
                                    id: childSnapshot.key,
                                    ...messageData
                                });
                            }
                        });
                    }

                    // Sort by timestamp and ensure unique messages
                    const uniqueMessages = messages
                        .filter((msg, index, arr) => arr.findIndex(m => m.id === msg.id) === index)
                        .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

                    callback(uniqueMessages);
                } catch (callbackError) {
                    console.log('Error processing messages:', callbackError);
                    callback([]);
                }
            }, (error) => {
                console.log('Error listening to messages:', error);
                callback([]);
            });

            // Store connection for cleanup
            this.activeConnections.set(chatId, unsubscribe);
            return unsubscribe;
        } catch (error) {
            console.log('Error setting up message listener:', error);
            return () => { };
        }
    }

    /**
     * Get all live support chats for admin
     * @returns {Promise<Array>} List of support chats
     */
    async getAllSupportChats() {
        try {
            if (!this.getDatabase()) {
                return [];
            }

            const supportChatsRef = ref(this.getDatabase(), 'liveSupport');
            const snapshot = await get(supportChatsRef);

            const chats = [];
            if (snapshot.exists()) {
                snapshot.forEach((childSnapshot) => {
                    const chatData = childSnapshot.val();
                    if (chatData.participants && chatData.participants.includes(SUPPORT_USER_ID)) {
                        chats.push({
                            id: childSnapshot.key,
                            ...chatData
                        });
                    }
                });
            }

            // Sort by last message timestamp
            chats.sort((a, b) => (b.lastMessageTimestamp || 0) - (a.lastMessageTimestamp || 0));
            return chats;
        } catch (error) {
            console.log('Error getting support chats:', error);
            return [];
        }
    }

    /**
     * Set user presence in live support chat
     * @param {string} chatId - Support chat ID
     * @param {string} userId - User ID
     * @param {boolean} isOnline - Online status
     */
    async setUserPresence(chatId, userId, isOnline = true) {
        try {
            if (!this.getDatabase()) return;

            const presenceRef = ref(this.getDatabase(), `liveSupport/${chatId}/participantsInfo/${userId}`);
            await update(presenceRef, {
                isOnline: isOnline,
                lastSeen: isOnline ? serverTimestamp() : Date.now()
            });

            if (isOnline) {
                // Set up disconnect handler
                const disconnectRef = ref(this.getDatabase(), `liveSupport/${chatId}/participantsInfo/${userId}`);
                await onDisconnect(disconnectRef).update({
                    isOnline: false,
                    lastSeen: Date.now()
                });
            }
        } catch (error) {
            console.log('Error setting user presence:', error);
        }
    }

    /**
     * Mark messages as read
     * @param {string} chatId - Support chat ID
     * @param {string} userId - User ID
     */
    async markAsRead(chatId, userId) {
        try {
            if (!this.getDatabase()) return;

            console.log('🔍 [DASHBOARD LIVE SUPPORT] Marking messages as read:', {
                chatId: chatId,
                userId: userId
            });

            // First, mark all unread messages as read
            const messagesRef = ref(this.getDatabase(), `liveSupport/${chatId}/messages`);
            const messagesSnapshot = await get(messagesRef);

            if (messagesSnapshot.exists()) {
                const updates = {};
                let hasUnreadMessages = false;

                messagesSnapshot.forEach((messageSnapshot) => {
                    const messageData = messageSnapshot.val();
                    const messageId = messageSnapshot.key;

                    // Mark messages as read if they are not from the current user and are unread
                    if (messageData.senderId !== userId && messageData.read === false) {
                        updates[`liveSupport/${chatId}/messages/${messageId}/read`] = true;
                        hasUnreadMessages = true;
                    }
                });

                // Apply message updates if there are unread messages
                if (hasUnreadMessages && Object.keys(updates).length > 0) {
                    await update(ref(this.getDatabase()), updates);
                    console.log('✅ [DASHBOARD LIVE SUPPORT] Messages marked as read:', Object.keys(updates).length);
                }
            }

            // Then update participant info
            const participantRef = ref(this.getDatabase(), `liveSupport/${chatId}/participantsInfo/${userId}`);

            // Check if participant exists before updating
            const participantSnapshot = await get(participantRef);
            if (!participantSnapshot.exists()) {
                console.warn('⚠️ [DASHBOARD LIVE SUPPORT] Participant does not exist for markAsRead');
                return;
            }

            await update(participantRef, {
                unread: false
            });

            console.log('✅ [DASHBOARD LIVE SUPPORT] Messages and participant info marked as read successfully');
        } catch (error) {
            console.log('❌ [DASHBOARD LIVE SUPPORT] Error marking as read:', error);
        }
    }

    /**
     * Get support chat details
     * @param {string} chatId - Support chat ID
     * @returns {Promise<Object>} Chat details
     */
    async getSupportChatDetails(chatId) {
        try {
            if (!this.getDatabase()) {
                return null;
            }

            const chatRef = ref(this.getDatabase(), `liveSupport/${chatId}`);
            const snapshot = await get(chatRef);

            if (snapshot.exists()) {
                return {
                    id: chatId,
                    ...snapshot.val()
                };
            }
            return null;
        } catch (error) {
            console.log('Error getting support chat details:', error);
            return null;
        }
    }

    /**
     * Permanently delete a support chat (admin only). Removes the whole node.
     */
    async deleteChat(chatId) {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }
            // Setting the node to null deletes it (admin role satisfies the rules).
            await set(ref(this.getDatabase(), `liveSupport/${chatId}`), null);
            if (this.activeConnections?.has(chatId)) {
                const unsub = this.activeConnections.get(chatId);
                if (typeof unsub === 'function') unsub();
                this.activeConnections.delete(chatId);
            }
            return { success: true };
        } catch (error) {
            console.log('Error deleting support chat:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Clean up active connections
     */
    cleanup() {
        this.activeConnections.forEach((unsubscribe) => {
            if (typeof unsubscribe === 'function') {
                unsubscribe();
            }
        });
        this.activeConnections.clear();
    }

    /**
     * Get all live support chats
     * @returns {Promise<Array>} Array of support chats
     */
    async getSupportChats() {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            const chatsRef = ref(this.getDatabase(), 'liveSupport');
            const snapshot = await get(chatsRef);

            if (snapshot.exists()) {
                const chats = [];
                snapshot.forEach((childSnapshot) => {
                    const chatData = childSnapshot.val();
                    chats.push({
                        id: childSnapshot.key,
                        ...chatData
                    });
                });
                return chats;
            }
            return [];
        } catch (error) {
            console.log('Error getting support chats:', error);
            return [];
        }
    }

    /**
     * Get Firebase support user details
     * @returns {Promise<Object|null>} Support user data or null
     */
    async getSupportUser() {
        try {
            // Ensure Firebase authentication before accessing database
            const isAuthenticated = await FirebaseAuthService.ensureAuthentication();
            if (!isAuthenticated) {
                console.log('❌ [LIVE SUPPORT] Firebase authentication required for getSupportUser');
                return null;
            }

            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            const supportUserRef = ref(this.getDatabase(), 'users/support');
            const snapshot = await get(supportUserRef);

            if (snapshot.exists()) {
                return snapshot.val();
            }
            return null;
        } catch (error) {
            console.log('Error getting support user:', error);
            return null;
        }
    }

    /**
     * Listen to all live support chats for real-time updates
     * @param {function} callback - Callback function to receive chat updates
     * @returns {function} Unsubscribe function
     */
    async listenToAllSupportChats(callback) {
        try {
            // Ensure Firebase authentication before accessing database
            const isAuthenticated = await FirebaseAuthService.ensureAuthentication();
            if (!isAuthenticated) {
                console.log('❌ [LIVE SUPPORT] Firebase authentication required for listenToAllSupportChats');
                callback([]);
                return () => { };
            }

            if (!this.getDatabase()) {
                console.log('Firebase database not initialized');
                return () => { };
            }

            const chatsRef = ref(this.getDatabase(), 'liveSupport');
            const unsubscribe = onValue(chatsRef, (snapshot) => {
                try {
                    const chats = [];
                    if (snapshot.exists()) {
                        snapshot.forEach((childSnapshot) => {
                            const chatData = childSnapshot.val();
                            chats.push({
                                id: childSnapshot.key,
                                ...chatData
                            });
                        });
                    }
                    callback(chats);
                } catch (callbackError) {
                    console.log('Error processing support chats:', callbackError);
                    callback([]);
                }
            }, (error) => {
                console.log('Error listening to support chats:', error);
                callback([]);
            });

            return unsubscribe;
        } catch (error) {
            console.log('Error setting up support chats listener:', error);
            return () => { };
        }
    }

    /**
     * Listen to chat active status changes
     * @param {string} chatId - Chat ID
     * @param {function} callback - Callback function
     * @returns {function} Unsubscribe function
     */
    listenToChatActiveStatus(chatId, callback) {
        try {
            if (!this.getDatabase()) {
                console.log('Firebase database not initialized');
                return () => { };
            }

            const chatRef = ref(this.getDatabase(), `liveSupport/${chatId}`);
            const unsubscribe = onValue(chatRef, (snapshot) => {
                try {
                    if (snapshot.exists()) {
                        const chatData = snapshot.val();
                        callback({
                            isActive: chatData.isActive || false,
                            lastActivity: chatData.lastActivity || null
                        });
                    }
                } catch (error) {
                    console.log('❌ [DASHBOARD LIVE SUPPORT] Error listening to chat active status:', error);
                }
            });

            return unsubscribe;
        } catch (error) {
            console.log('Error setting up chat active status listener:', error);
            return () => { };
        }
    }

    /**
     * Set user online status in Firebase users collection
     * @param {string} userId - User ID
     * @param {boolean} isOnline - Online status
     */
    async setUserOnlineStatus(userId, isOnline) {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            const userRef = ref(this.getDatabase(), `users/${userId}`);
            await update(userRef, {
                isOnline: isOnline,
                lastSeen: Date.now()
            });

            console.log(`✅ [DASHBOARD LIVE SUPPORT] User ${userId} online status set to: ${isOnline}`);
            return true;
        } catch (error) {
            console.log('Error setting user online status:', error);
            return false;
        }
    }

    /**
     * Set chat active status
     * @param {string} chatId - Chat ID
     * @param {boolean} isActive - Whether chat is active
     */
    async setChatActiveStatus(chatId, isActive) {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            console.log('🔍 [DASHBOARD LIVE SUPPORT] Setting chat active status:', {
                chatId: chatId,
                isActive: isActive
            });

            const chatRef = ref(this.getDatabase(), `liveSupport/${chatId}`);
            await update(chatRef, {
                isActive: isActive,
                lastActivity: Date.now()
            });

            console.log('✅ [DASHBOARD LIVE SUPPORT] Chat active status updated successfully');
            return true;
        } catch (error) {
            console.log('❌ [DASHBOARD LIVE SUPPORT] Error setting chat active status:', error);
            return false;
        }
    }

    /**
     * Update participant info in chat
     * @param {string} chatId - Chat ID
     * @param {string} userId - User ID
     * @param {Object} participantInfo - Participant information
     */
    async updateParticipantInfo(chatId, userId, participantInfo) {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            console.log('🔍 [DASHBOARD LIVE SUPPORT] Updating participant info:', {
                chatId: chatId,
                userId: userId,
                participantInfo: participantInfo
            });

            const participantRef = ref(this.getDatabase(), `liveSupport/${chatId}/participantsInfo/${userId}`);

            // Check if participant exists
            const participantSnapshot = await get(participantRef);
            if (!participantSnapshot.exists()) {
                console.warn('⚠️ [DASHBOARD LIVE SUPPORT] Participant does not exist for update');
                return false;
            }

            const currentData = participantSnapshot.val();

            // Update participant info with new data while preserving existing fields
            await update(participantRef, {
                ...participantInfo,
                lastUpdated: Date.now()
            });

            console.log('✅ [DASHBOARD LIVE SUPPORT] Participant info updated successfully');
            return true;
        } catch (error) {
            console.log('❌ [DASHBOARD LIVE SUPPORT] Error updating participant info:', error);
            return false;
        }
    }

    /**
     * Set chat presence for a user in a specific chat
     * @param {string} chatId - Chat ID
     * @param {string} userId - User ID
     * @param {boolean} isActive - Whether user is actively viewing the chat
     */
    async setChatPresence(chatId, userId, isActive) {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            const presenceRef = ref(this.getDatabase(), `liveSupport/${chatId}/presence/${userId}`);
            await set(presenceRef, {
                userId: userId,
                isActive: isActive,
                timestamp: serverTimestamp(),
                lastActivity: Date.now()
            });

            // Set up onDisconnect to mark user as inactive when they disconnect
            if (isActive) {
                await onDisconnect(presenceRef).update({
                    isActive: false,
                    timestamp: serverTimestamp(),
                    lastActivity: Date.now()
                });
            }


            return true;
        } catch (error) {
            console.log('Error setting chat presence:', error);
            return false;
        }
    }

    /**
     * Get chat presence for all users in a chat
     * @param {string} chatId - Chat ID
     * @returns {Promise<Object>} Presence data
     */
    async getChatPresence(chatId) {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            const presenceRef = ref(this.getDatabase(), `liveSupport/${chatId}/presence`);
            const snapshot = await get(presenceRef);

            if (snapshot.exists()) {
                return snapshot.val();
            }
            return {};
        } catch (error) {
            console.log('Error getting chat presence:', error);
            return {};
        }
    }

    /**
     * Listen to chat presence changes
     * @param {string} chatId - Chat ID
     * @param {function} callback - Callback function
     * @returns {function} Unsubscribe function
     */
    listenToChatPresence(chatId, callback) {
        try {
            if (!this.getDatabase()) {
                console.log('Firebase database not initialized');
                return () => { };
            }

            const presenceRef = ref(this.getDatabase(), `liveSupport/${chatId}/presence`);
            const unsubscribe = onValue(presenceRef, (snapshot) => {
                try {
                    const presence = snapshot.exists() ? snapshot.val() : {};
                    callback(presence);
                } catch (callbackError) {
                    console.log('Error processing chat presence:', callbackError);
                    callback({});
                }
            }, (error) => {
                console.log('Error listening to chat presence:', error);
                callback({});
            });

            return unsubscribe;
        } catch (error) {
            console.log('Error setting up chat presence listener:', error);
            return () => { };
        }
    }

    /**
     * Get global presence for a user
     * @param {string} userId - User ID
     * @returns {Promise<Object|null>} Global presence data
     */
    async getGlobalPresence(userId) {
        try {
            if (!this.getDatabase()) {
                throw new Error('Firebase database not initialized');
            }

            const presenceRef = ref(this.getDatabase(), `globalPresence/${userId}`);
            const snapshot = await get(presenceRef);

            if (snapshot.exists()) {
                return snapshot.val();
            }
            return null;
        } catch (error) {
            console.log('Error getting global presence:', error);
            return null;
        }
    }

    /**
     * Listen to global presence changes for a user
     * @param {string} userId - User ID
     * @param {function} callback - Callback function
     * @returns {function} Unsubscribe function
     */
    listenToGlobalPresence(userId, callback) {
        try {
            if (!this.getDatabase()) {
                console.log('Firebase database not initialized');
                return () => { };
            }

            const presenceRef = ref(this.getDatabase(), `globalPresence/${userId}`);
            const unsubscribe = onValue(presenceRef, (snapshot) => {
                try {
                    const presence = snapshot.exists() ? snapshot.val() : null;
                    callback(presence);
                } catch (callbackError) {
                    console.log('Error processing global presence:', callbackError);
                    callback(null);
                }
            }, (error) => {
                console.log('Error listening to global presence:', error);
                callback(null);
            });

            return unsubscribe;
        } catch (error) {
            console.log('Error setting up global presence listener:', error);
            return () => { };
        }
    }
}

export default new LiveSupportService();
